'use client';

import { useTranslations } from 'next-intl';

import { UserAvatar } from '@/components/images/user-avatar';
import type { UserIdentityShape } from '@/components/profile/user-identity';
import { UserIdentity } from '@/components/profile/user-identity';
import { Icon } from '@/components/ui/icon';
import { ArrowUp01IconSolid, FireIconSolid } from '@/components/ui/icon/glyphs';
import { onEvent } from '@/events';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { cn } from '@/lib/ui/utils';

import {
  RIGHT_COLUMN_CARD_GLOW_BASE,
  RIGHT_COLUMN_CARD_LINK_CHROME,
} from './right-column-card-chrome';

export interface BreakingViewsRowProps {
  /** 1-based carousel slot — forwarded to analytics. */
  position: 1 | 2 | 3 | 4 | 5;
  signature: string;
  marketPubkey: string;
  content: string;
  marketTitle: string;
  trader: UserIdentityShape;
  tradeDirection: 'buy' | 'sell';
  tradeAmountD6: number;
  outcomeLabel: string;
  /** Signed thesis rating, rendered bottom-right of the author row. */
  rating: number;
  /** When true, renders the HOT TAKES heading inside the card link. */
  showHeading?: boolean;
}

export function BreakingViewsRow({
  position,
  signature,
  marketPubkey,
  content,
  marketTitle,
  trader,
  tradeDirection,
  tradeAmountD6,
  outcomeLabel,
  rating,
  showHeading = true,
}: BreakingViewsRowProps): React.ReactNode {
  const t = useTranslations('markets.breaking_views');
  const { formatD6 } = useFormattedCurrency();
  const href: string = `/markets/${marketPubkey}?tab=debate&thesis=${signature}`;
  // Full route prefetch on hover/focus/touch intent (the row is prefetch={false}
  // — no viewport prefetch storm from the carousel).
  const intent = useIntentRoutePrefetch(href);

  const tradeLine =
    tradeDirection === 'buy'
      ? t('trade_line_buy', {
          amount: formatD6(String(tradeAmountD6)),
          outcome: outcomeLabel,
        })
      : t('trade_line_sell', {
          amount: formatD6(String(tradeAmountD6)),
          outcome: outcomeLabel,
        });

  function handleClick(): void {
    onEvent('breaking_view_tapped', {
      marketPubkey,
      signature,
      position,
    });
  }

  return (
    <Link
      href={href}
      prefetch={false}
      {...intent}
      aria-label={t('aria_row_label', { marketTitle })}
      data-testid="breaking-views-row"
      onClick={handleClick}
      className={cn(RIGHT_COLUMN_CARD_LINK_CHROME, 'flex h-full flex-col gap-2')}
    >
      <div
        aria-hidden="true"
        data-testid="breaking-views-row-glow"
        className={cn(
          RIGHT_COLUMN_CARD_GLOW_BASE,
          'bg-[radial-gradient(ellipse_85%_75%_at_100%_0%,var(--feedback-error-accent)_0%,transparent_68%)]',
        )}
      />
      {showHeading ? (
        <div className="flex min-w-0 items-center gap-1.5 pr-8" data-testid="breaking-views-header">
          <Icon
            icon={FireIconSolid}
            aria-hidden="true"
            className="size-3.5 shrink-0 text-outcome-red-cta dark:text-outcome-red-text"
          />
          <h2 className="text-caption font-medium uppercase tracking-wide text-foreground-tertiary">
            {t('heading')}
          </h2>
        </div>
      ) : null}
      <p
        data-testid="breaking-views-row-content"
        // Natural 2-line clamp (with its own ellipsis); the flex-1 spacer below
        // absorbs the remaining height so the fixed-height card never grows.
        className="mt-1 line-clamp-2 text-body font-normal leading-snug text-foreground-primary"
      >
        {content}
      </p>
      <div className="flex-1" aria-hidden="true" />
      <div className="h-px w-full bg-border-secondary" aria-hidden="true" />
      <div className="flex min-w-0 items-center gap-2" data-testid="breaking-views-row-trader">
        <UserAvatar
          userId={trader.userId}
          displayName={trader.displayName}
          avatarUrl={trader.avatarUrl}
          size="sm"
          rasterise={trader.avatarPreferences?.rasterise ?? false}
          nsfw={trader.avatarNsfw ?? false}
          {...(trader.walletAddress !== undefined ? { colorSeed: trader.walletAddress } : {})}
        />
        <div className="min-w-0 flex flex-col">
          <UserIdentity
            user={{ ...trader, handle: null }}
            variant="inline"
            size="sm"
            nameClassName="truncate text-body text-foreground-primary"
          />
          <span
            data-testid="breaking-views-row-trade-line"
            className="truncate text-caption text-foreground-secondary"
          >
            {tradeLine}
          </span>
        </div>
        <div
          data-testid="breaking-views-row-rating"
          role="img"
          aria-label={t('rating_label', { count: rating })}
          className="ml-auto flex shrink-0 items-center gap-1 text-foreground-secondary"
        >
          <Icon icon={ArrowUp01IconSolid} aria-hidden="true" className="size-3.5" />
          <span aria-hidden="true" className="text-caption font-medium tabular-nums">
            {rating}
          </span>
        </div>
      </div>
    </Link>
  );
}
